import React from 'react';
import { Pressable, View } from 'react-native';

import { Avatar, Button, Heading, Text, VStack } from '@squire/ui';

import { StarIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { AppointmentRatingStarsStepProps } from './types';

const AppointmentRatingStarsStep: React.FC<AppointmentRatingStarsStepProps> = ({
  professionalPhotoUrl,
  professionalFirstName,
  formattedDateTime,
  selectedRating,
  onRatingSelect,
  onNextStep,
  pinActionButton = true,
}) => {
  const { t } = useScreenTranslations('AppointmentRatingScreen');

  return (
    <VStack
      style={pinActionButton ? styles.centeredContent : styles.flowContent}
    >
      <View
        style={[
          styles.contentBlock,
          pinActionButton && styles.contentBlockPinned,
        ]}
      >
        <Avatar
          uri={professionalPhotoUrl}
          size="xl"
          shape="square"
          fallbackText={professionalFirstName}
          style={styles.avatarContainer}
        />
        <Heading
          variant="l"
          accessibilityLabel={t('step1.heading', {
            firstName: professionalFirstName,
          })}
          numberOfLines={3}
          ellipsizeMode="tail"
          style={styles.mainHeading}
        >
          {t('step1.heading', { firstName: professionalFirstName })}
        </Heading>
        <Text variant="m" color="textSecondary" style={styles.dateText}>
          {formattedDateTime}
        </Text>
        <View style={styles.starsContainer}>
          {[1, 2, 3, 4, 5].map((rating) => (
            <Pressable
              key={rating}
              onPress={() => onRatingSelect(rating)}
              style={styles.starButton}
              accessibilityRole="button"
              accessibilityLabel={t('accessibility.starButton', {
                count: rating,
              })}
              accessibilityHint={t('accessibility.starButtonHint', {
                count: rating,
              })}
            >
              <StarIcon
                size={60}
                color={
                  selectedRating !== null && rating <= selectedRating
                    ? 'iconBrand'
                    : 'backgroundL1'
                }
              />
            </Pressable>
          ))}
        </View>
      </View>
      {selectedRating && (
        <Button
          variant="primary"
          size="medium"
          onPress={onNextStep}
          style={styles.actionButton}
          accessibilityLabel={t('accessibility.nextButton')}
          accessibilityHint={t('accessibility.nextButtonHint')}
        >
          {t('step1.nextButton')}
        </Button>
      )}
    </VStack>
  );
};

AppointmentRatingStarsStep.displayName = 'AppointmentRatingStarsStep';
export { AppointmentRatingStarsStep };
